import { useEffect, useState } from "react";
import { api, uploadImage } from "./api";
import type { DayBundle, MealStatus } from "./types";
import { TYPE_COLOR } from "./types";
import { PlanEditModal } from "./PlanEditModal";

export function DayCard({
  bundle,
  initiallyExpanded,
  onMutated,
}: {
  bundle: DayBundle;
  initiallyExpanded: boolean;
  onMutated: () => Promise<void> | void;
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [editing, setEditing] = useState(false);
  // When the parent swaps in a different day, re-apply the expansion preference
  // so the new card opens.
  useEffect(() => {
    setExpanded(initiallyExpanded);
  }, [bundle.plan.dateISO, initiallyExpanded]);
  const [busy, setBusy] = useState(false);
  const day = bundle.plan;
  const tint = TYPE_COLOR[day.type];

  function status(idx: number): MealStatus {
    return (bundle.log?.meals?.find((m) => m.plannedIndex === idx)?.status as MealStatus) ?? "planned";
  }
  function loggedMeal(idx: number) {
    return bundle.log?.meals?.find((x) => x.plannedIndex === idx);
  }
  function effectiveCal(idx: number): number {
    return loggedMeal(idx)?.overrideCal ?? day.meals[idx]?.cal ?? 0;
  }
  function effectivePro(idx: number): number {
    return loggedMeal(idx)?.overrideProtein ?? day.meals[idx]?.pro ?? 0;
  }
  function mealNote(idx: number): string {
    return loggedMeal(idx)?.note ?? "";
  }
  const totalCal = day.meals.reduce(
    (s, _, i) => (status(i) === "skipped" ? s : s + effectiveCal(i)),
    0,
  ) + (bundle.log?.meals ?? [])
      .filter((m) => m.plannedIndex === null && m.status !== "skipped")
      .reduce((s, m) => s + (m.overrideCal ?? 0), 0);
  const totalPro = day.meals.reduce(
    (s, _, i) => (status(i) === "skipped" ? s : s + effectivePro(i)),
    0,
  ) + (bundle.log?.meals ?? [])
      .filter((m) => m.plannedIndex === null && m.status !== "skipped")
      .reduce((s, m) => s + (m.overrideProtein ?? 0), 0);

  const exerciseDone = (i: number) => bundle.log?.completedExerciseIndices?.includes(i) ?? false;
  const exerciseActual = (i: number) => bundle.log?.exerciseActuals?.[String(i)];
  const totalActualBurn = Object.values(bundle.log?.exerciseActuals ?? {})
    .reduce((s, a) => s + (Number(a?.kcal) || 0), 0);
  const effectiveBurn = totalActualBurn > 0 ? totalActualBurn : (day.training.calBurn ?? 0);
  const netKcal = totalCal - effectiveBurn;

  async function withMutation(op: () => Promise<unknown>) {
    setBusy(true);
    try { await op(); await onMutated(); }
    catch (e) { alert(String(e)); }
    finally { setBusy(false); }
  }

  function cycleMeal(idx: number) {
    const next: MealStatus = status(idx) === "planned" ? "eaten" : status(idx) === "eaten" ? "skipped" : "planned";
    void withMutation(() => api.patchMeal(day.dateISO, idx, { status: next }));
  }

  function editMeal(idx: number) {
    const note = prompt(
      `What did you actually have for "${day.meals[idx]?.name ?? ""}"? (leave blank if as planned)`,
      mealNote(idx),
    );
    if (note === null) return;
    const cal = Number(prompt("kcal:", String(effectiveCal(idx))));
    if (!Number.isFinite(cal)) return;
    const pro = Number(prompt("protein g:", String(effectivePro(idx))));
    if (!Number.isFinite(pro)) return;
    const trimmed = note.trim();
    void withMutation(() =>
      api.patchMeal(day.dateISO, idx, {
        status: "eaten",
        overrideCal: cal,
        overrideProtein: pro,
        note: trimmed.length === 0 ? null : trimmed,
      }),
    );
  }

  function addAdHoc() {
    const name = prompt("Meal name:")?.trim();
    if (!name) return;
    const cal = Number(prompt("kcal:"));
    if (!Number.isFinite(cal)) return;
    const pro = Number(prompt("protein g:"));
    if (!Number.isFinite(pro)) return;
    const noteRaw = prompt("Note (optional):") ?? "";
    const note = noteRaw.trim();
    void withMutation(() =>
      api.postAdHocMeal(day.dateISO, {
        name, cal, pro, status: "eaten",
        note: note.length === 0 ? undefined : note,
      })
    );
  }

  function uploadMealPic(idx: number) {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      const note = prompt("Note (e.g. what you actually ate):") ?? undefined;
      try {
        await uploadImage(f, { dateISO: day.dateISO, kind: "meal", mealIndex: idx, note });
        alert("Uploaded.");
      } catch (e) { alert(String(e)); }
    };
    input.click();
  }

  return (
    <div className={`day-card ${expanded ? "expanded" : ""}`} style={expanded ? { borderColor: tint + "80" } : undefined}>
      <div className="day-header" onClick={() => setExpanded((e) => !e)}>
        <div className="day-icon-box" style={{ background: tint + "26" }}>
          {day.training.icon ?? "•"}
        </div>
        <div className="day-meta">
          <div className="day-date">{day.date}</div>
          <div className="day-badges">
            <span className="pill" style={{ background: tint + "33", color: tint }}>{day.type.toUpperCase()}</span>
            <span style={{ color: "var(--sub)", fontSize: 12 }}>{day.training.title}</span>
          </div>
        </div>
        <div className="day-totals">
          <div className="day-kcal">{Math.round(totalCal)} kcal</div>
          <div className="day-prot">{Math.round(totalPro)}g P</div>
        </div>
      </div>

      {expanded && (
        <div className="day-body">
          <div className="training-block">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div className="training-title" style={{ color: tint }}>{day.training.title}</div>
              <button className="btn" style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => setEditing(true)}>✎ edit</button>
            </div>
            {(day.exercises ?? []).map((ex, i) => {
              const actual = exerciseActual(i);
              return (
                <div
                  key={i}
                  className={`exercise-row ${exerciseDone(i) ? "done" : ""}`}
                  onClick={() => withMutation(() => api.patchExercise(day.dateISO, i, !exerciseDone(i)))}
                >
                  <span style={{ fontSize: 18 }}>{exerciseDone(i) ? "☑" : "☐"}</span>
                  <span style={{ fontSize: 16 }}>{ex.icon}</span>
                  <span className="exercise-label">{ex.label}</span>
                  <div style={{ marginLeft: "auto", textAlign: "right", fontSize: 11 }}>
                    {actual?.kcal != null && (
                      <div style={{ color: "var(--orange)", fontFamily: "DM Mono, monospace", fontWeight: 700 }}>
                        {Math.round(actual.kcal)} kcal
                      </div>
                    )}
                    {(actual?.durationMin != null || actual?.distanceKm != null) && (
                      <div style={{ color: "var(--sub)" }}>
                        {actual?.durationMin != null ? `${Math.round(actual.durationMin)} min` : ""}
                        {actual?.durationMin != null && actual?.distanceKm != null ? " · " : ""}
                        {actual?.distanceKm != null ? `${actual.distanceKm.toFixed(1)} km` : ""}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {day.training.note && (
              <div style={{ fontSize: 12, color: "var(--muted)", borderLeft: "2px solid var(--muted)", paddingLeft: 8 }}>
                {day.training.note}
              </div>
            )}
            <div className="training-cal">
              ~{Math.round(day.training.calBurn)} kcal planned
              {totalActualBurn > 0 && (
                <span style={{ color: "var(--green)", marginLeft: 8 }}>
                  · {Math.round(totalActualBurn)} actual
                </span>
              )}
            </div>
          </div>

          <div>
            <div style={{ color: "var(--muted)", fontSize: 10, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>MEALS</div>
            {day.meals.map((meal, idx) => {
              const s = status(idx);
              const note = mealNote(idx);
              return (
                <div key={idx}>
                  <div className={`meal-row ${s === "skipped" ? "skipped" : ""}`}>
                    <button className="tick-btn" onClick={() => cycleMeal(idx)} title="Tick">
                      {s === "eaten" ? "✓" : s === "skipped" ? "✗" : "○"}
                    </button>
                    <div className="meal-time-col">
                      <div className="meal-time">{meal.time}</div>
                      <div style={{ fontSize: 16 }}>{meal.icon ?? "🍽"}</div>
                    </div>
                    <div className="meal-content">
                      <div className={`meal-name ${s === "skipped" ? "strike" : ""}`}>
                        {meal.name}
                        {meal.careem && <span className="pill" style={{ marginLeft: 6, background: "rgba(251,146,60,0.2)", color: "var(--orange)" }}>CAREEM</span>}
                      </div>
                      {meal.items.map((it, i) => <div key={i} className="meal-item">· {it}</div>)}
                    </div>
                    <div className="meal-macros">
                      <div className="kcal">{Math.round(effectiveCal(idx))}</div>
                      <div className="prot">{Math.round(effectivePro(idx))}g P</div>
                      <div style={{ display: "flex", gap: 4, marginTop: 4, justifyContent: "flex-end" }}>
                        <button className="btn" style={{ padding: "2px 6px" }} onClick={() => editMeal(idx)}>✎</button>
                        <button className="btn" style={{ padding: "2px 6px" }} onClick={() => uploadMealPic(idx)}>📷</button>
                      </div>
                    </div>
                  </div>
                  {note && (
                    <div style={{ paddingLeft: 98, paddingBottom: 8, color: "var(--purple)", fontSize: 12 }}>
                      💬 {note}
                    </div>
                  )}
                </div>
              );
            })}
            {(bundle.log?.meals ?? []).filter((m) => m.plannedIndex === null).map((m, i) => (
              <div key={`adhoc-${i}`}>
                <div className="meal-row">
                  <span style={{ width: 24, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--purple)" }}>◆</span>
                  <div className="meal-content">
                    <div className="meal-name">{m.adhocName}</div>
                    <span className="pill" style={{ background: "rgba(167,139,250,0.2)", color: "var(--purple)" }}>AD-HOC</span>
                  </div>
                  <div className="meal-macros">
                    <div className="kcal">{Math.round(m.overrideCal ?? 0)}</div>
                    <div className="prot">{Math.round(m.overrideProtein ?? 0)}g P</div>
                  </div>
                </div>
                {m.note && (
                  <div style={{ paddingLeft: 34, paddingBottom: 8, color: "var(--purple)", fontSize: 12 }}>
                    💬 {m.note}
                  </div>
                )}
              </div>
            ))}
            <button className="ad-hoc-button" onClick={addAdHoc} disabled={busy}>＋ Add meal</button>
          </div>
          <div className="day-net">
            <div className="net-cell" style={{ borderColor: "var(--yellow)" }}>
              <div className="net-label">IN</div>
              <div className="net-value" style={{ color: "var(--yellow)" }}>{Math.round(totalCal)}</div>
              <div className="net-trail">kcal eaten</div>
            </div>
            <div className="net-cell" style={{ borderColor: "var(--orange)" }}>
              <div className="net-label">OUT</div>
              <div className="net-value" style={{ color: "var(--orange)" }}>{Math.round(effectiveBurn)}</div>
              <div className="net-trail">{totalActualBurn > 0 ? "actual" : "planned"}</div>
            </div>
            <div className="net-cell" style={{ borderColor: netKcal <= day.target ? "var(--green)" : "var(--red)" }}>
              <div className="net-label">NET</div>
              <div className="net-value" style={{ color: netKcal <= day.target ? "var(--green)" : "var(--red)" }}>
                {netKcal >= 0 ? "+" : ""}{Math.round(netKcal)}
              </div>
              <div className="net-trail">vs {day.target} target</div>
            </div>
          </div>
        </div>
      )}
      {editing && (
        <PlanEditModal
          day={day}
          onClose={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await onMutated(); }}
        />
      )}
    </div>
  );
}
